/**
 * Pricing Service - Manages dynamic pricing configuration and A/B testing
 */
import * as amplitude from "@amplitude/analytics-browser";
import { getFlowPricingSetId, getStoredFunnelFlow } from "./funnelFlow";
import { getDiscountPercent } from "./discountPercent";
import type {
  Cents,
  PricingConfig,
  PricingPrice,
  PricingSet,
} from "@/types/pricing.type";
import type { SupportedCurrency } from "@/lib/currency";
import { detectUserCurrency } from "@/lib/currency";
import { PRICING_DATA } from "@/data/pricing.data";

class PricingService {
  static COHORT_STORAGE_KEY = "pricing_cohort";

  static getConfig(): PricingConfig {
    return PRICING_DATA;
  }

  /**
   * Pricing sets still being served. A set kept in the config at weight 0 is
   * retired: its provider price ids are likely disabled, so cohort assignment
   * must never land on it — neither by draw nor through a stale stored cohort.
   * Retired sets stay reachable through the opt-in lookups below
   * (`getPricingSetById`), which is how a standalone funnel (exp_wmq) pins
   * its own pricing while sitting at weight 0.
   */
  static getActivePricingSets(): Array<PricingSet> {
    return this.getConfig().pricingSets.filter((set) => set.weight > 0);
  }

  /**
   * Assigns a user to a pricing cohort based on weights
   * Uses weighted random selection to distribute users across active pricing sets
   */
  static assignCohort(): PricingSet {
    const config = this.getConfig();
    const activeSets = this.getActivePricingSets();

    // Misconfigured catalog (every set retired) — better a price than nothing
    const sets = activeSets.length > 0 ? activeSets : config.pricingSets;

    // If A/B testing is not enabled, return the first active pricing set
    if (!config.abTestEnabled || sets.length === 1) return sets[0];

    // Weighted random selection
    const totalWeight = sets.reduce((sum, set) => sum + set.weight, 0);
    let random = Math.random() * totalWeight;

    for (const pricingSet of sets) {
      random -= pricingSet.weight;
      if (random <= 0) {
        return pricingSet;
      }
    }

    // Fallback to first active set
    return sets[0];
  }

  /**
   * Gets or creates the user's pricing cohort
   * Cohort assignment is persistent and stored in localStorage
   */
  static getUserPricingSet(): PricingSet {
    if (typeof window === "undefined") return this.assignCohort();

    // A standalone funnel pins its own set (weight-agnostic lookup: those sets
    // sit at weight 0 on purpose) instead of following the cohort draw.
    const pinnedSetId = getFlowPricingSetId(getStoredFunnelFlow());
    // Try to get existing cohort from localStorage
    const storedCohort =
      pinnedSetId === null
        ? localStorage.getItem(this.COHORT_STORAGE_KEY)
        : null;
    if (pinnedSetId !== null) {
      const pricingSet = this.getPricingSetById(pinnedSetId);

      if (pricingSet) return pricingSet;
      else
        console.warn(
          "Pinned pricing set no longer exists in config, reassigning",
        );
    } else if (storedCohort) {
      try {
        const cohortData = JSON.parse(storedCohort);

        // Verify the cohort is still being served — a retired set stays in the
        // config, so an existence check would keep serving its disabled price ids
        const pricingSet = this.getActivePricingSets().find(
          (set) => set.id === cohortData.id,
        );

        if (pricingSet) return pricingSet;
        else console.warn("Stored cohort is no longer active, reassigning");
      } catch (error) {
        console.error("Error parsing stored cohort:", error);
      }
    }

    // Assign new cohort
    const assignedSet = this.assignCohort();

    // Store cohort assignment
    const cohortData = {
      id: assignedSet.id,
      name: assignedSet.name,
      assignedAt: new Date().toISOString(),
    };

    localStorage.setItem(this.COHORT_STORAGE_KEY, JSON.stringify(cohortData));

    // Track cohort assignment in Amplitude
    this.trackCohortAssignment(assignedSet.id);

    return assignedSet;
  }

  /**
   * Tracks the user's pricing cohort assignment in Amplitude
   * Sets a user property that persists across all events
   * @param {string} cohortId - The ID of the pricing cohort
   */
  static trackCohortAssignment(cohortId: string) {
    this.addAmplitudeUserProperty("priceCohort", cohortId);
  }

  static addAmplitudeUserProperty(key: string, value: string) {
    if (typeof amplitude === "undefined") {
      console.warn("Amplitude not available for cohort tracking");
      return;
    }

    try {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set(key, value);
      amplitude.identify(identifyEvent);
    } catch (error) {
      console.error("Error tracking user property in Amplitude:", error);
    }
  }

  static removeAmplitudeUserProperty(key: string) {
    if (typeof amplitude === "undefined") return;

    try {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.unset(key);
      amplitude.identify(identifyEvent);
    } catch (error) {
      console.error("Error removing user property in Amplitude:", error);
    }
  }

  /**
   * Gets all available prices for the current user's cohort
   * @returns {Array} Array of price objects sorted by sortOrder
   */
  static getUserPrices(): Array<PricingPrice> {
    const pricingSet = this.getUserPricingSet();
    return [...pricingSet.prices].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Weight-agnostic lookup of a pricing set — how `getUserPricingSet` serves
   * the set a standalone funnel (?flow=exp_wmq) pins at weight 0.
   */
  static getPricingSetById(id: string): PricingSet | null {
    const config = this.getConfig();
    return config.pricingSets.find((set) => set.id === id) ?? null;
  }

  /**
   * Gets a specific price by ID for the current user's cohort
   * @param {string} priceId - The ID of the price to retrieve (e.g., 'weekly', 'monthly')
   * @returns {PricingPrice|null} The price object or null if not found
   */
  static getPriceById(priceId: string): PricingPrice | null {
    const pricingSet = this.getUserPricingSet();

    return pricingSet.prices.find((price) => price.id === priceId) || null;
  }

  static getUserCurrency(): SupportedCurrency {
    return detectUserCurrency();
  }

  /**
   * Resolves display/charge amounts for a plan using optional per-currency catalog data.
   */
  static resolvePriceForCurrency(
    price: PricingPrice,
    currency: SupportedCurrency = detectUserCurrency(),
  ): {
    originalPrice: Cents;
    discountedPrice: Cents | null;
    dailyPrice: Cents;
    originalDailyPrice: Cents;
  } {
    const localized = price.localizedAmounts?.[currency];
    const originalPrice: Cents =
      localized !== undefined ? localized.originalPrice : price.originalPrice;

    let discountedPrice: Cents | null;
    if (localized !== undefined) discountedPrice = localized.discountedPrice;
    else discountedPrice = price.discountedPrice;

    // All arithmetic stays on integer cents — floor to whole cents so the
    // per-day figure never introduces fractional-cent float error.
    const periodDays = price.durationDays;
    const effectiveIntro = discountedPrice ?? originalPrice;
    const dailyPrice = Math.floor(effectiveIntro / periodDays) as Cents;
    const originalDailyPrice = Math.floor(originalPrice / periodDays) as Cents;
    return {
      originalPrice,
      discountedPrice,
      dailyPrice,
      originalDailyPrice,
    };
  }

  static getPriceAmounts(
    planId: string,
    currency: SupportedCurrency = detectUserCurrency(),
  ): {
    originalPrice: Cents;
    discountedPrice: Cents | null;
    dailyPrice: Cents;
    originalDailyPrice: Cents;
  } | null {
    const price = this.getPriceById(planId);
    if (!price) return null;
    return this.resolvePriceForCurrency(price, currency);
  }

  /**
   * Stripe price id, per currency when the plan ships one.
   */
  static getPriceId(
    planId: string,
    currency?: SupportedCurrency,
  ): string | null {
    const price = this.getPriceById(planId);
    if (!price) return null;

    const meta = price.stripe;
    if (currency !== undefined && meta.localizedPriceIds?.[currency])
      return meta.localizedPriceIds[currency] ?? null;

    return meta.priceId;
  }

  /**
   * Stripe discount / promo id.
   */
  static getDiscountId(planId: string): string | null {
    const price = this.getPriceById(planId);
    if (!price) return null;
    return price.stripe.discountId;
  }

  /**
   * Forces a cohort reassignment (useful for testing)
   * WARNING: This will reassign the user to a new cohort
   */
  static forceReassignCohort(): void {
    if (typeof window === "undefined") return;

    localStorage.removeItem(this.COHORT_STORAGE_KEY);
    console.info(
      "Cohort cleared, will reassign on next getUserPricingSet() call",
    );
  }

  /**
   * Gets information about the current A/B test setup
   * @returns {Object} A/B test configuration details
   */
  static getAbTestInfo(): {
    enabled: boolean;
    numberOfVariants: number;
    currentCohort: {
      id: string;
      name: string;
    };
    allVariants: Array<{
      id: PricingSet["id"];
      name: PricingSet["name"];
      weight: PricingSet["weight"];
      priceCount: number;
    }>;
  } {
    const config = this.getConfig();
    const currentCohort = this.getUserPricingSet();

    return {
      enabled: config.abTestEnabled,
      numberOfVariants: config.pricingSets.length,
      currentCohort: {
        id: currentCohort.id,
        name: currentCohort.name,
      },
      allVariants: config.pricingSets.map((set) => ({
        id: set.id,
        name: set.name,
        weight: set.weight,
        priceCount: set.prices.length,
      })),
    };
  }

  /**
   * Validates the pricing configuration
   * @returns {boolean} isValid - Whether the configuration is valid
   * @returns {string[]} errors - Any errors found in the configuration
   */
  static validateConfig(): { isValid: boolean; errors: Array<string> } {
    const config = this.getConfig();
    const errors = [];

    // Check if pricingSets exist
    if (config.pricingSets.length === 0) {
      errors.push("No pricing sets defined");
      return { isValid: false, errors };
    }

    // If A/B testing is enabled, validate weights
    if (config.abTestEnabled && config.pricingSets.length > 1) {
      const totalWeight = config.pricingSets.reduce(
        (sum, set) => sum + set.weight,
        0,
      );

      if (totalWeight !== 100) {
        errors.push(`Total weight must equal 100, got ${totalWeight}`);
      }
    }

    // Validate each pricing set
    config.pricingSets.forEach((set, index) => {
      if (!set.id) {
        errors.push(`Pricing set at index ${index} missing id`);
      }

      if (set.prices.length === 0) {
        errors.push(`Pricing set '${set.id}' has no prices defined`);
      }

      if (set.prices.length > 3) {
        errors.push(
          `Pricing set '${set.id}' has more than 3 prices (${set.prices.length})`,
        );
      }

      // Validate each price
      set.prices.forEach((price, priceIndex) => {
        const requiredFields = [
          "id",
          "duration",
          "discountedPrice",
          "stripe",
        ] as const;
        requiredFields.forEach((field) => {
          if (!(field in price)) {
            errors.push(
              `Price at index ${priceIndex} in set '${set.id}' missing ${field}`,
            );
          }
        });
      });
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Checks if any price in the current pricing set has a discount
   * @returns {boolean} True if at least one price has a discountedPrice field
   */
  static hasDiscounts(): boolean {
    const prices = this.getUserPrices();
    return prices.some((price) => price.discountedPrice !== null);
  }

  /**
   * Checks if a specific price has a discount
   * @param {string} priceId - The ID of the price to check
   * @returns {boolean} True if the price has a discountedPrice field
   */
  static priceHasDiscount(priceId: string): boolean {
    const price = this.getPriceById(priceId);
    return price ? price.discountedPrice !== null : false;
  }

  /**
   * Amount charged at intro price in cents (discount if present, else list).
   */
  static getDisplayPrice(priceId: string): Cents | null {
    const amounts = this.getPriceAmounts(priceId);
    if (!amounts) return null;
    return amounts.discountedPrice ?? amounts.originalPrice;
  }

  /**
   * Calculates the discount percentage for a price (locale-aware).
   */
  static getDiscountPercentage(priceId: string): number | null {
    const price = this.getPriceById(priceId);
    const amounts = this.getPriceAmounts(priceId);
    if (!price || amounts === null || amounts.discountedPrice === null)
      return null;

    const { originalPrice, discountedPrice } = amounts;
    if (originalPrice === 0 || discountedPrice >= originalPrice) return null;

    return getDiscountPercent(discountedPrice, originalPrice);
  }
}

export default PricingService;
